import axios from 'axios';

import { pomConfig } from '@/configs/pom-config';
import { useBoundStore } from '@/stores/store';
import { userManager } from '@/user-manager';

async function getAccessToken(): Promise<string | null> {
  try {
    const currentUser = await userManager.getUser();
    return currentUser?.access_token || null;
  } catch {
    return null;
  }
}

const api = axios.create({
  baseURL: pomConfig.http.api,
  httpsAgent: pomConfig.env !== 'development',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const loadingId = config.url || 'unknown-request';
    useBoundStore.getState().setLoading({ id: loadingId });

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    const loadingId = response.config.url || 'unknown-request';
    useBoundStore.getState().removeLoading(loadingId);
    return response;
  },
  async (error) => {
    const loadingId = error.config?.url || 'unknown-request';
    useBoundStore.getState().removeLoading(loadingId);

    if (error.response?.status === 401) {
      try {
        const newUser = await userManager.signinSilent();
        if (newUser?.access_token) {
          error.config.headers.Authorization = `Bearer ${newUser.access_token}`;
          return api
            .request(error.config)
            .catch(async (replayError: unknown) => {
              if (
                axios.isAxiosError(replayError) &&
                replayError.response?.status === 401
              ) {
                await userManager.signoutRedirect();
              }
              // Preserve replay errors, including business errors after renewal.
              return Promise.reject(replayError);
            });
        }
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          await userManager.signoutRedirect();
        }
      }
    }

    return Promise.reject(error);
  },
);

export default api;
